import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  getUserIdFromRefreshToken,
  InfoBar,
  type InviteModalInvalidEmailAdded,
  isTouchDevice,
  Link,
  LogSentryMessage,
  Modal,
  type ModalProps,
  Select,
  ServiceError,
  TagInput,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { sessionStorageKeys } from "../../config";
import useNavigation from "../../hooks/navigation";
import { TeamsUserRoles, TeamsUserRolesDisplayNames } from "../../hooks/useAccountTypes";
import { useDebouncedValue } from "../../hooks/useDebounce";
import { useEventListener } from "../../hooks/useEventListener";
import usePrevious from "../../hooks/usePrevious";
import { useHasStudioPricing } from "../../hooks/usePriceConfig";
import {
  useTeam,
  useTeamMembers,
  useTeamsInvitationsEstimate,
  useTeamsInvitationsMutation,
  useTeamSubscriptions,
} from "../../hooks/useTeams";
import type { TeamsInvitationsEstimateResponseData } from "../../hooks/useTeamsTypes";
import { useTracker } from "../../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { ServiceErrorCode, SubscriptionPeriod, SubscriptionTier } from "../../types";
import { inQuotes } from "../../utils/convertUtils";
import { isEmailInvalid } from "../../utils/validationUtils";
import { SeatUpgradeEstimateSummary } from "../EstimateSummaries/SeatUpgradeEstimateSummary/SeatUpgradeEstimateSummary";
import { OverlayProvider } from "../InfoTooltip/InfoTooltip";
import PaymentUpdateModal from "../PaymentUpdateModal/PaymentUpdateModal";
import { InvitePromo } from "./components/InvitePromo";

import style from "./TeamInviteModal.module.css";

type TeamInvitationEstimateErrorFilter = ({ code }: { code: string }) => boolean;

const isFormatInvalidErrorFilter: TeamInvitationEstimateErrorFilter = ({ code }) =>
  code === ServiceErrorCode.InvalidEmail || code === ServiceErrorCode.ValidationError;

const isForbiddenDomainErrorFilter: TeamInvitationEstimateErrorFilter = ({ code }) =>
  code === ServiceErrorCode.ForbiddenDomainForContributorInvitation;

const isAlreadyMemberErrorFilter: TeamInvitationEstimateErrorFilter = ({ code }) =>
  code === ServiceErrorCode.UserAlreadyInvitedToTeam ||
  code === ServiceErrorCode.UserAlreadyInThisTeam;

type EmailWithReason = {
  email: string;
  reason: InviteModalInvalidEmailAdded["eventProperties"]["reason"];
};

const schema = z.object({
  emails: z.array(z.string()).superRefine((val, ctx) => {
    val.forEach((email, index) => {
      if (isEmailInvalid(email)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: email,
          path: [index],
          fatal: false,
        });

        return null;
      }
    });
  }),
  role: z.enum([TeamsUserRoles.Editor, TeamsUserRoles.Contributor]),
});

export type TeamInviteModalFormSchema = z.infer<typeof schema>;

type TeamInviteModalProps = Omit<ModalProps, "size" | "persistent" | "children" | "title"> & {
  teamID: string | undefined;
  subtitle?: string;
};

export const MAX_CONTRIBUTORS_ALLOWED = 10;

export const TeamInviteModal: React.FC<TeamInviteModalProps> = ({ open, teamID, onClose }) => {
  const { teamData, isOwner, isSeatManager } = useTeam(teamID);
  const { data: teamMembersData } = useTeamMembers(teamID);
  const { isStudioTier } = useHasStudioPricing();
  const { data: teamSubscriptionsData, isLoading: isTeamSubscriptionsLoading } =
    useTeamSubscriptions(teamID);
  const tracker = useTracker();
  const navigation = useNavigation();

  const [isPaymentUpdateModalOpen, setIsPaymentUpdateModalOpen] = useState(false);
  const [isInvitationNotAvailable, setIsInvitationNotAvailable] = useState(false);
  const [overrideContributorInvite, setOverrideContributorInvite] = useState(false);

  const [shouldSendEstimateRecievedTelemetry, setShouldSendEstimateReceivedTelemetry] =
    useState(false);

  const [currentInputValue, setCurrentInputValue] = useState("");
  const [isCurrentInputValid, setIsCurrentInputValid] = useState(false);

  const isContributorInviteEligible = Boolean(
    (isOwner || isSeatManager) &&
    !teamSubscriptionsData?.hasSalesTeamSubscription &&
    (teamSubscriptionsData?.seatBasedTeamSubscription?.editorSeats ?? 0) < 2 &&
    !teamData?.addons?.syncForbidden
  );

  const isEditorInviteEligible =
    !teamSubscriptionsData?.individualSubscriptions?.some((sub) => {
      return sub.tier === SubscriptionTier.Individual;
    }) && teamSubscriptionsData?.seatBasedTeamSubscription?.tier !== SubscriptionTier.Individual;
  const isContributorInviteFlow = isContributorInviteEligible && !overrideContributorInvite;

  const {
    handleSubmit,
    formState: { isSubmitting, errors, isSubmitted },
    watch,
    control,
    reset,
    register,
    setValue,
  } = useForm<TeamInviteModalFormSchema>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      emails: [],
      role: isContributorInviteFlow ? TeamsUserRoles.Contributor : TeamsUserRoles.Editor,
    },
  });

  useEffect(() => {
    if (isContributorInviteFlow) {
      setValue("role", TeamsUserRoles.Contributor);
    } else {
      setValue("role", TeamsUserRoles.Editor);
    }
  }, [isContributorInviteFlow, isEditorInviteEligible, setValue]);

  const [backendErrors, setBackendErrors] = useState<
    Exclude<TeamsInvitationsEstimateResponseData["errors"], undefined>
  >([]);
  useEffect(() => {
    setBackendErrors([]);
  }, [overrideContributorInvite]);

  const {
    mutateAsync: teamsInvitiationsMutation,
    isError: isTeamsInvitiationsMutationError,
    isPending: isTeamsInvitationsMutationPending,
    reset: resetTeamsInvitationsMutation,
  } = useTeamsInvitationsMutation(teamID);

  const emails = watch("emails");
  const role = watch("role");

  const { validEmails, invalidEmails, invalidEmailsWithReason } = useMemo((): {
    validEmails: string[];
    invalidEmails: string[];
    invalidEmailsWithReason: EmailWithReason[];
  } => {
    const teamMemberEmails = teamMembersData?.users.map((user) => user.email) ?? [];
    const teamMemberEmailsWithReason: EmailWithReason[] = teamMemberEmails?.map<EmailWithReason>(
      (email) => ({
        email,
        reason: "already_in_team",
      })
    );

    const formErrorEmailsWithReason: EmailWithReason[] =
      errors?.emails
        ?.map?.<
          EmailWithReason | null | undefined
        >((error) => (error?.message ? { email: error.message, reason: "invalid_format" } : null))
        .filter((v): v is NonNullable<typeof v> => Boolean(v)) ?? [];
    const formErrorEmails = formErrorEmailsWithReason.map((error) => error.email);

    const backendInvalidEmails = backendErrors.map((error) => error.email);
    const backendContributorForbiddenEmailsWithReason: EmailWithReason[] = backendErrors
      .filter(isForbiddenDomainErrorFilter)
      .map<EmailWithReason>((error) => ({
        email: error.email,
        reason: "ineligible_domain",
      }));
    const backendFormatInvalidEmailsWithReason: EmailWithReason[] = backendErrors
      .filter(isFormatInvalidErrorFilter)
      .map<EmailWithReason>((error) => ({
        email: error.email,
        reason: "invalid_format",
      }));
    const backendAlreadyMemberInvalidEmailsWithReason: EmailWithReason[] = backendErrors
      .filter(isAlreadyMemberErrorFilter)
      .map<EmailWithReason>((error) => ({
        email: error.email,
        reason: "already_in_team",
      }));

    const backendInvalidEmailsWithReason = [
      ...backendContributorForbiddenEmailsWithReason,
      ...backendFormatInvalidEmailsWithReason,
      ...backendAlreadyMemberInvalidEmailsWithReason,
    ];
    const backendInvalidEmailsWithoutReason: EmailWithReason[] = backendErrors
      .filter(
        (error) =>
          !backendInvalidEmailsWithReason.some(
            (emailWithReason) => emailWithReason.email === error.email
          )
      )
      .map<EmailWithReason>((error) => ({
        email: error.email,
        reason: undefined,
      }));

    const invalidEmails = [...formErrorEmails, ...backendInvalidEmails, ...teamMemberEmails];
    const invalidEmailsWithReason: EmailWithReason[] = [
      ...formErrorEmailsWithReason,
      ...teamMemberEmailsWithReason,
      ...backendInvalidEmailsWithReason,
      ...backendInvalidEmailsWithoutReason,
    ];

    const allEmails = [...emails];
    if (isCurrentInputValid && currentInputValue) {
      allEmails.push(currentInputValue);
    }
    return {
      validEmails: allEmails.filter((email) => !invalidEmails.includes(email)),
      invalidEmails,
      invalidEmailsWithReason,
    };
  }, [
    emails,
    backendErrors,
    teamMembersData?.users,
    errors?.emails,
    currentInputValue,
    isCurrentInputValid,
  ]);

  const tagValidEmails = useMemo(() => {
    if (isCurrentInputValid && currentInputValue) {
      return validEmails.filter((e) => e !== currentInputValue);
    }
    return validEmails;
  }, [validEmails, isCurrentInputValid, currentInputValue]);

  const prevTagValidEmails = usePrevious(tagValidEmails);
  const prevEmails = usePrevious(emails);
  const previousInvalidEmailsWithReason = usePrevious(invalidEmailsWithReason);

  useEffect(() => {
    if (isSubmitting || isSubmitted) {
      return;
    }
    const validEmailChangeCount = tagValidEmails.length - (prevTagValidEmails?.length ?? 0);

    if (validEmailChangeCount > 0) {
      tracker.trackInviteModalEmailAdded({ emailCount: validEmailChangeCount, role });
      return;
    }

    const emailChangeCount = emails.length - (prevEmails?.length ?? 0);
    if (emailChangeCount < 0) {
      tracker.trackInviteModalEmailRemoved({ emailCount: -emailChangeCount });
      return;
    }

    // When the email is already in the invalid emails list
    if (emailChangeCount > 0) {
      const emailDiff = emails.filter((email) => !prevEmails?.includes(email));

      emailDiff.forEach((email) => {
        const emailWithReason = invalidEmailsWithReason.find(
          (emailWithReason) => emailWithReason.email === email
        );

        if (emailWithReason) {
          tracker.trackInviteModalInvalidEmailAdded({
            emailCount: 1,
            reason: emailWithReason.reason,
            role,
          });
        }
      });

      return;
    }

    // When adding a new invalid email for the first time
    const invalidEmailChangeCount =
      invalidEmailsWithReason.length - (previousInvalidEmailsWithReason?.length ?? 0);
    if (invalidEmailChangeCount > 0) {
      const invalidEmailsDiff = invalidEmailsWithReason.filter(
        (emailWithReason) =>
          !previousInvalidEmailsWithReason?.some(
            (prevEmailWithReason) => prevEmailWithReason.email === emailWithReason.email
          )
      );

      invalidEmailsDiff.forEach((emailWithReason) => {
        // We pre-populate the invalid emails list with the already in team emails, so we need to make sure to only send the event if it was typed in by the user
        if (!emails.includes(emailWithReason.email)) {
          return;
        }
        tracker.trackInviteModalInvalidEmailAdded({
          emailCount: 1,
          reason: emailWithReason.reason,
          role,
        });
      });

      return;
    }
  }, [
    role,
    validEmails,
    prevTagValidEmails,
    invalidEmails,
    prevEmails,
    emails,
    tracker,
    isSubmitting,
    isSubmitted,
    invalidEmailsWithReason,
    previousInvalidEmailsWithReason,
    tagValidEmails.length,
  ]);

  const { numberOfAvailableSeats, additionalEditorSeatCount } = useMemo(() => {
    const editorSeats = teamSubscriptionsData?.seatBasedTeamSubscription?.editorSeats ?? 0;
    const editorSeatsUsed = teamSubscriptionsData?.seatBasedTeamSubscription?.editorSeatsUsed ?? 0;

    const numberOfOriginallyAvailableSeats = editorSeats - editorSeatsUsed;

    const newEditorCount = validEmails.length * Number(role === TeamsUserRoles.Editor);
    const numberOfAvailableSeats = numberOfOriginallyAvailableSeats - newEditorCount;
    const additionalEditorSeatCount = newEditorCount - numberOfOriginallyAvailableSeats;

    return { numberOfAvailableSeats, additionalEditorSeatCount };
  }, [teamSubscriptionsData?.seatBasedTeamSubscription, validEmails, role]);

  const emailsForEstimate = useMemo(() => {
    const emailsSet = new Set([...validEmails]);
    return Array.from(emailsSet);
  }, [validEmails]);

  const debounceDelay = 300;
  const debouncedEmailsForEstimate = useDebouncedValue(emailsForEstimate, debounceDelay);

  const {
    data: estimateData,
    isFetching: isEstimateFetching,
    isPaused: isEstimatePaused,
    isError: isEstimateError,
    isSuccess: isEstimateSuccess,
    dataUpdatedAt: estimateDataUpdatedAt,
  } = useTeamsInvitationsEstimate(teamID, debouncedEmailsForEstimate, role);

  const isEstimateStale = useMemo(() => {
    if (emailsForEstimate.length === 0) {
      return false;
    }

    if (debouncedEmailsForEstimate.length !== emailsForEstimate.length) {
      return true;
    }

    const currentEmailsSet = new Set(emailsForEstimate);
    const debouncedEmailsSet = new Set(debouncedEmailsForEstimate);

    if (currentEmailsSet.size !== debouncedEmailsSet.size) {
      return true;
    }

    for (const email of currentEmailsSet) {
      if (!debouncedEmailsSet.has(email)) {
        return true;
      }
    }

    return false;
  }, [emailsForEstimate, debouncedEmailsForEstimate]);

  const previousEstimateDataUpdatedAt = usePrevious(estimateDataUpdatedAt);

  useEffect(() => {
    if (estimateData && shouldSendEstimateRecievedTelemetry) {
      const formatInvalidEmailCount = invalidEmailsWithReason.filter(
        ({ reason, email }) => reason === "invalid_format" && emails.includes(email)
      ).length;
      const memberInvalidEmailCount = invalidEmailsWithReason.filter(
        ({ reason, email }) => reason === "already_in_team" && emails.includes(email)
      ).length;
      const recordedInvalidEmailCount = invalidEmailsWithReason.length;

      tracker.trackInviteModalEstimateReceived({
        emailCount: emails.length,
        contributorBlockedEmailCount: estimateData.contributorBlockedEmailCount,
        validEmailCount: validEmails.length,
        role,
        formatInvalidEmailCount,
        memberInvalidEmailCount,
        recordedInvalidEmailCount,
      });

      setShouldSendEstimateReceivedTelemetry(false);
    }
  }, [
    previousEstimateDataUpdatedAt,
    estimateDataUpdatedAt,
    isSubmitted,
    isSubmitting,
    shouldSendEstimateRecievedTelemetry,
    setShouldSendEstimateReceivedTelemetry,
    estimateData,
    emails,
    validEmails,
    role,
    tracker,
    invalidEmailsWithReason,
  ]);

  useEffect(() => {
    if (isEstimateError || isTeamsInvitiationsMutationError) {
      const errorDetail = isEstimateError ? "estimate_error" : "invitation_error";
      tracker.trackInviteModalError(errorDetail);
    }
  }, [isEstimateError, isTeamsInvitiationsMutationError, tracker]);

  const { seatUpgradeEstimate, newSubscriptionEstimate, salesTeamSeatUpgradeEstimate } =
    useMemo(() => {
      const newSubscriptionEstimate = estimateData?.newSubscriptionEstimate;
      const seatUpgradeEstimate = estimateData?.seatUpgradeEstimate;
      const salesTeamSeatUpgradeEstimate = estimateData?.salesTeamSeatUpgradeEstimate;

      return { seatUpgradeEstimate, newSubscriptionEstimate, salesTeamSeatUpgradeEstimate };
    }, [estimateData]);

  const contributorCount = useMemo(() => {
    return (
      teamMembersData?.users?.filter((user) => user.role === TeamsUserRoles.Contributor).length ?? 0
    );
  }, [teamMembersData?.users]);

  const hasReachedContributorLimit = useMemo(() => {
    const pendingContributorsCount =
      Number(role === TeamsUserRoles.Contributor) * validEmails.length;
    return contributorCount + pendingContributorsCount > MAX_CONTRIBUTORS_ALLOWED;
  }, [contributorCount, role, validEmails.length]);

  const invalidDomainForContributorInvitationCount = useMemo(() => {
    const emailsSet = new Set(emails);
    if (isCurrentInputValid && currentInputValue) {
      emailsSet.add(currentInputValue);
    }

    return backendErrors?.filter(
      (error) =>
        error.code === ServiceErrorCode.ForbiddenDomainForContributorInvitation &&
        emailsSet.has(error.email)
    ).length;
  }, [backendErrors, emails, isCurrentInputValid, currentInputValue]);

  const previousOpen = usePrevious(open);
  useEffect(() => {
    if (
      /*
        There is a race-condition here, if we set the backend errors before the estimate data is updated,
        we will end up re-populating the backend errors with the previous response's errors.
      */
      (estimateDataUpdatedAt === previousEstimateDataUpdatedAt &&
        /*
        But we need to repopulate the backend errors if the modal is closed and opened again, because the last query state is kept in cache
      */
        (previousOpen || !open)) ||
      isEstimateFetching
    ) {
      return;
    }

    const newBackendErrors =
      estimateData?.errors?.filter((error) => !backendErrors.includes(error)) ?? [];

    if (newBackendErrors.length > 0) {
      setBackendErrors([...backendErrors, ...newBackendErrors]);
    }

    if (
      estimateData &&
      estimateDataUpdatedAt !== previousEstimateDataUpdatedAt &&
      !isSubmitted &&
      !isSubmitting
    ) {
      setShouldSendEstimateReceivedTelemetry(true);
    }
  }, [
    backendErrors,
    estimateData?.errors,
    open,
    previousOpen,
    estimateDataUpdatedAt,
    previousEstimateDataUpdatedAt,
    isEstimateFetching,
    isSubmitted,
    isSubmitting,
    estimateData,
  ]);

  const isInvitationSuccess = useRef(false);

  const beforeUnloadListener = useCallback(
    () =>
      tracker.trackInviteAbandonment({
        initiatedFrom: "page_abandonment",
        contributorBlockedEmailCount: invalidDomainForContributorInvitationCount,
        emailCount: emails.length,
        validEmailCount: validEmails.length,
      }),
    [tracker, invalidDomainForContributorInvitationCount, emails, validEmails]
  );

  useEventListener("beforeunload", beforeUnloadListener);

  const resetState = useCallback(() => {
    // Keep the current role in case contributor invite flow is overridden (after editor invite the modal resetted to contributor, the next modal open contained wrong role)
    reset({ emails: [], role });
    setBackendErrors([]);
    setIsPaymentUpdateModalOpen(false);
    setCurrentInputValue("");
    setIsCurrentInputValid(false);
    if (!isInvitationSuccess.current) {
      tracker.trackInviteAbandonment({
        initiatedFrom: "close",
        contributorBlockedEmailCount: invalidDomainForContributorInvitationCount,
        emailCount: emails.length,
        validEmailCount: validEmails.length,
      });
    }
    resetTeamsInvitationsMutation();
    setOverrideContributorInvite(false);
  }, [
    reset,
    tracker,
    resetTeamsInvitationsMutation,
    emails,
    validEmails,
    invalidDomainForContributorInvitationCount,
    role,
  ]);

  const overrideContributorInviteCallback = useCallback(() => {
    setOverrideContributorInvite(true);
    tracker.trackInviteAbandonment({
      initiatedFrom: "role_change",
      contributorBlockedEmailCount: invalidDomainForContributorInvitationCount,
      emailCount: emails.length,
      validEmailCount: validEmails.length,
    });

    tracker.trackInviteModalShown({
      role: TeamsUserRoles.Editor,
    });
  }, [tracker, invalidDomainForContributorInvitationCount, emails, validEmails]);

  const submit = useCallback(
    async ({ type }: { type: "successCallback" | "errorCallback" }) => {
      if (isCurrentInputValid && currentInputValue) {
        if (!emails.includes(currentInputValue) && !invalidEmails.includes(currentInputValue)) {
          tracker.trackInviteModalEmailAdded({ emailCount: 1, role });
        }
      }

      tracker.trackInviteModalSubmitted({
        contributorBlockedEmailCount: estimateData?.contributorBlockedEmailCount,
        emailCount: validEmails.length,
        role,
      });

      const estimate = { ...estimateData };
      delete estimate.errors;

      LogSentryMessage({
        message: `TeamInviteModal submit: ${type}`,
        level: "info",
        extra: { type, estimateData: { ...estimate }, userID: getUserIdFromRefreshToken() },
      });

      if (isEstimateSuccess && !estimateData?.available) {
        tracker.trackInviteModalError("invitation_not_available");
        setIsInvitationNotAvailable(true);
        return;
      }

      if (newSubscriptionEstimate) {
        const checkoutTier = (): SubscriptionTier => {
          if (!isStudioTier) return SubscriptionTier.Lite;
          if (role === "contributor") return SubscriptionTier.Individual;
          return SubscriptionTier.Studio;
        };

        const inviteData = {
          emails: validEmails,
          role,
        };
        sessionStorage.setItem(sessionStorageKeys.inviteData, JSON.stringify(inviteData));
        onClose?.();
        navigation.goToCheckout({
          period: SubscriptionPeriod.Yearly,
          showPeriodPicker: true,
          utmSource: "team_invite",
          withInvite: true,
          selectedTier: checkoutTier(),
        });
        return;
      }

      try {
        await teamsInvitiationsMutation({
          emails: validEmails,
          role,
        });
        isInvitationSuccess.current = true;
        onClose?.();
        tracker.trackInviteSuccess();
        toast.success("Invitation successfully sent!");
        setCurrentInputValue("");
        setIsCurrentInputValid(false);
      } catch (error) {
        if (
          error instanceof ServiceError &&
          error.hasCode(ServiceErrorCode.PaymentProcessingFailed)
        ) {
          setIsPaymentUpdateModalOpen(true);
          return;
        }
      }
    },
    [
      isCurrentInputValid,
      currentInputValue,
      tracker,
      estimateData,
      validEmails,
      role,
      isEstimateSuccess,
      newSubscriptionEstimate,
      emails,
      invalidEmails,
      onClose,
      navigation,
      isStudioTier,
      teamsInvitiationsMutation,
    ]
  );

  const successSubmitCallback = useCallback(() => submit({ type: "successCallback" }), [submit]);
  const errorSubmitCallback = useCallback(() => submit({ type: "errorCallback" }), [submit]);
  const submitAfterPaymentUpdate = useCallback(async () => {
    await handleSubmit(successSubmitCallback, errorSubmitCallback)();
  }, [handleSubmit, successSubmitCallback, errorSubmitCallback]);

  const handleInputChange = useCallback((value: string) => {
    const trimmedValue = value.trim();
    setCurrentInputValue(trimmedValue);
    const isInvalid = isEmailInvalid(trimmedValue);
    setIsCurrentInputValid(!isInvalid);
  }, []);

  useEffect(() => {
    if (!open && previousOpen) {
      if (isPaymentUpdateModalOpen) {
        return;
      }
      resetState();
    }
  }, [open, resetState, previousOpen, isPaymentUpdateModalOpen]);

  useEffect(() => {
    if (!previousOpen && open) {
      tracker.trackInviteModalShown({
        role,
      });
    }
  }, [open, previousOpen, role, tracker]);

  const hasPendingErrors = useMemo(() => {
    const emailsInInput = new Set(emails);
    const newErrorsFromEstimate =
      estimateData?.errors?.filter(
        (error) =>
          emailsInInput.has(error.email) && !backendErrors.some((e) => e.email === error.email)
      ) ?? [];
    return newErrorsFromEstimate.length > 0;
  }, [estimateData?.errors, backendErrors, emails]);

  const isSubmitDisabled = useMemo(() => {
    const hasInvalidInput = currentInputValue !== "" && !isCurrentInputValid;
    const hasValidEmails = validEmails.length > 0;

    return (
      isSubmitting ||
      hasInvalidInput ||
      !hasValidEmails ||
      isEstimateFetching ||
      isEstimatePaused ||
      isEstimateError ||
      isInvitationNotAvailable ||
      isTeamsInvitationsMutationPending ||
      !isEstimateSuccess ||
      hasReachedContributorLimit ||
      isEstimateStale ||
      hasPendingErrors
    );
  }, [
    currentInputValue,
    isCurrentInputValid,
    validEmails.length,
    isSubmitting,
    isEstimateFetching,
    isEstimatePaused,
    isEstimateError,
    isInvitationNotAvailable,
    isTeamsInvitationsMutationPending,
    isEstimateSuccess,
    hasReachedContributorLimit,
    isEstimateStale,
    hasPendingErrors,
  ]);

  const infoBar = (() => {
    if (numberOfAvailableSeats < 0 && teamSubscriptionsData?.hasGracePeriodTeamSubscription)
      return (
        <InfoBar
          variant="critical"
          title="Inviting new members is unavailable."
          text="Your subscription is within the grace period. Update your card details to invite new members and buy new seats."
        />
      );

    if (
      numberOfAvailableSeats < 0 &&
      teamSubscriptionsData?.hasNonAutoRenewingSelfServeTeamSubscription
    )
      return (
        <InfoBar
          variant="critical"
          title="Inviting new members is unavailable."
          text="Your subscription has been canceled. Turn on auto-renewal to invite new members and buy new seats."
        />
      );

    if (hasReachedContributorLimit && role === TeamsUserRoles.Contributor)
      return (
        <InfoBar
          variant="attention"
          text={
            <>
              <span>
                You’ve assigned all available Contributor seats. You can add up-to{" "}
                {MAX_CONTRIBUTORS_ALLOWED} free Contributors to your team.
                {isEditorInviteEligible && <> You can invite additional members as Editors.</>}
              </span>
              {isEditorInviteEligible && (
                <Link onClick={overrideContributorInviteCallback}> Invite as Editor...</Link>
              )}
            </>
          }
        />
      );

    if (
      isContributorInviteFlow &&
      Boolean(invalidDomainForContributorInvitationCount) &&
      isEditorInviteEligible
    )
      return (
        <InfoBar
          variant="attention"
          text={
            <>
              <span>
                The free Contributor role is only available with a company email address. Try a
                different email address or
              </span>
              <Link onClick={overrideContributorInviteCallback}> Invite as Editor...</Link>
            </>
          }
        />
      );

    if (isEstimateError || isTeamsInvitiationsMutationError)
      return (
        <InfoBar
          variant="warning"
          title="Invitation is unavailable at the moment."
          text="Please reload the page or try again later if the issue persists."
        />
      );

    if (isInvitationNotAvailable || (!isEditorInviteEligible && !isContributorInviteEligible))
      return (
        <InfoBar
          variant="warning"
          title="Invitation is unavailable at the moment."
          text="Click below to learn more how to start collaborating with your team."
        />
      );

    return null;
  })();

  return (
    <>
      <Modal
        className={style.modal}
        open={open && !isTeamSubscriptionsLoading}
        onClose={() => {
          if (isPaymentUpdateModalOpen) {
            return;
          }
          setIsInvitationNotAvailable(false);
          onClose?.();
        }}
        title={"Invite members"}
        size="medium"
      >
        <OverlayProvider>
          {!isContributorInviteFlow && !teamSubscriptionsData?.hasSalesTeamSubscription && (
            <InvitePromo
              type="editor"
              period={
                teamSubscriptionsData?.seatBasedTeamSubscription?.subscriptionPeriod ??
                teamSubscriptionsData?.individualSubscriptions?.[0]?.subscriptionPeriod
              }
            />
          )}
          {isContributorInviteFlow && <InvitePromo type="contributor" />}
          <form
            className={style.form}
            onSubmit={handleSubmit(successSubmitCallback, errorSubmitCallback)}
          >
            <div>
              <div className={style.inputRow}>
                <Controller
                  control={control}
                  name="emails"
                  render={({ field: { name, onBlur, onChange, value } }) => {
                    return (
                      <TagInput
                        name={name}
                        values={value}
                        placeholder="Emails"
                        autoFocus={!isTouchDevice()}
                        className={style.textField}
                        tokenizeOnBlur={false}
                        onBlur={(_emails) => {
                          onBlur();
                        }}
                        maxRows={8}
                        onChange={onChange}
                        errors={invalidEmails}
                        onAdd={() => {
                          setCurrentInputValue("");
                          setIsCurrentInputValid(false);
                        }}
                        onInput={(e) => handleInputChange(e.currentTarget.value)}
                      />
                    );
                  }}
                />
                <Select
                  {...register("role")}
                  className={classNames(style.roleSelect, style.hidden)}
                  onClick={() => {
                    tracker.trackButtonClick({
                      action: ElementClickAction.SELECT_ROLE,
                      location: ElementClickLocation.INVITE_MEMBERS_MODAL,
                    });
                  }}
                >
                  {isEditorInviteEligible && (
                    <option value={TeamsUserRoles.Editor}>
                      {TeamsUserRolesDisplayNames.editor}
                    </option>
                  )}
                  {isContributorInviteFlow && (
                    <option value={TeamsUserRoles.Contributor}>
                      {TeamsUserRolesDisplayNames.contributor}
                    </option>
                  )}
                </Select>
              </div>
            </div>
            {numberOfAvailableSeats < 0 &&
              seatUpgradeEstimate &&
              teamSubscriptionsData?.seatBasedTeamSubscription && (
                <div>
                  <SeatUpgradeEstimateSummary
                    proratedEstimate={seatUpgradeEstimate.prorated}
                    renewalEstimate={seatUpgradeEstimate.renewal}
                    renewalDate={seatUpgradeEstimate.renewalDate}
                    subscription={teamSubscriptionsData?.seatBasedTeamSubscription}
                    currencySymbol={estimateData?.currencySymbol}
                    additionalEditorSeatCount={additionalEditorSeatCount}
                    paymentSource={seatUpgradeEstimate.primaryPaymentSource}
                    isLoading={isEstimateFetching}
                  />
                </div>
              )}
            {salesTeamSeatUpgradeEstimate &&
              teamSubscriptionsData?.seatBasedTeamSubscription &&
              teamSubscriptionsData?.seatBasedTeamSubscription.editorSeatsUsed +
                validEmails.length >
                teamSubscriptionsData?.seatBasedTeamSubscription.editorSeats && (
                <p className={classNames(style.text, style.tertiary)}>
                  Your additional team seats may affect your current plan.
                </p>
              )}
            {infoBar}
            <div className={style.ctaRow}>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitDisabled}
                loading={isEstimateFetching || isTeamsInvitationsMutationPending}
                onClick={() => {
                  tracker.trackButtonClick({
                    action: ElementClickAction.CONFIRM,
                    location: ElementClickLocation.INVITE_MEMBERS_MODAL,
                    details: `${validEmails.length}`,
                  });
                }}
              >
                {(newSubscriptionEstimate ?? teamSubscriptionsData?.hasNoSubscription)
                  ? `Continue`
                  : `Invite`}
              </Button>
            </div>
          </form>
        </OverlayProvider>
      </Modal>
      <PaymentUpdateModal
        teamID={teamID}
        open={isPaymentUpdateModalOpen}
        onClose={() => {
          setIsPaymentUpdateModalOpen(false);
        }}
        initialError={
          <InfoBar
            variant="critical"
            title="Payment error, invitation failed."
            text="Update your card details to invite members."
          />
        }
        tertiaryText={
          seatUpgradeEstimate && seatUpgradeEstimate.prorated
            ? `Your card will get charged ${estimateData?.currencySymbol}${
                seatUpgradeEstimate.prorated.totalAmount
              } when you click ${inQuotes("Update & invite")}. ${
                seatUpgradeEstimate.prorated.currencyCode === "USD"
                  ? `All prices in ${seatUpgradeEstimate.prorated.currencyCode}.`
                  : ""
              }`
            : undefined
        }
        ctaText="Update & invite"
        onSuccess={async () => {
          await submitAfterPaymentUpdate();
        }}
      />
    </>
  );
};
